/**
 * Forsikringsoversikten (VIB-2917, plan 3.10). Forsikringsåret følger
 * kalenderåret, så forsikringen gjelder til og med 31.12.
 *
 * Regel for å havne i oversikten: «Forsikret til» er innen 90 dager
 * (Utløper), eller utstyret har ingen gyldig forsikring (Ikke forsikret).
 * Solgt og utrangert utstyr og utstyr merket «Ikke forny» er ute av begge.
 */
import type { EquipmentStatus } from "./constants";

export const INSURANCE_WARNING_DAYS = 90;

export type InsuranceState = "insured" | "expiring" | "not_insured" | "excluded";

interface InsurableItem {
  status: EquipmentStatus;
  insured_until: string | null;
  insurance_declined: boolean;
}

const toIsoDate = (d: Date): string => d.toISOString().slice(0, 10);

function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return toIsoDate(d);
}

export function insuranceState(item: InsurableItem, today: string): InsuranceState {
  if (item.status === "sold" || item.status === "retired" || item.insurance_declined) return "excluded";
  if (!item.insured_until || item.insured_until < today) return "not_insured";
  if (item.insured_until <= addDays(today, INSURANCE_WARNING_DAYS)) return "expiring";
  return "insured";
}

/** «Forny»: ett kalenderår til, regnet fra det som er dekket i dag. */
export function renewalDate(insuredUntil: string | null, today: string): string {
  const base = Math.max(Number((insuredUntil ?? today).slice(0, 4)), Number(today.slice(0, 4)));
  return `${base + 1}-12-31`;
}

/** «Forsikre»: ut inneværende år, eller ut neste år når årsskiftet er innen 90 dager. */
export function newInsuranceDate(today: string): string {
  const year = Number(today.slice(0, 4));
  const endOfYear = `${year}-12-31`;
  return endOfYear <= addDays(today, INSURANCE_WARNING_DAYS) ? `${year + 1}-12-31` : endOfYear;
}

export function todayIso(now: Date = new Date()): string {
  return now.toLocaleDateString("sv-SE", { timeZone: "Europe/Oslo" });
}
